import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { masterChartPath } from 'constants/masterChartRoutes';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ArrowBackRounded } from '@mui/icons-material';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import { contentCardSx } from 'styles/standaloneLayoutTokens';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import ProvenanceInfoButton from 'components/individual/health-records/ProvenanceInfoButton';

const OtherHistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const { control, getValues, reset } = useForm({
    defaultValues: {
      smokingStatus: '',
      alcoholUse: '',
      drugUse: '',
      occupation: '',
      familyHistory: '',
    },
  });

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      setLoading(true);
      try {
        const doc = await db.collection('users').doc(user.uid).get();
        const hr = doc.data()?.healthRecords || {};
        setFullHealthRecords(hr);
        const other = hr.other || {};
        reset({
          smokingStatus: other.smokingStatus || '',
          alcoholUse: other.alcoholUse || '',
          drugUse: other.drugUse || '',
          occupation: other.occupation || '',
          familyHistory: other.familyHistory || '',
        });
      } catch {
        setError(
          'Unable to load your history — retry before editing or saving (a failed load can look like blank fields).',
        );
        setFullHealthRecords(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid, reset]);

  const loadFailed = Boolean(error) && fullHealthRecords == null;

  const handleSave = useCallback(async () => {
    if (!user?.uid || saving || fullHealthRecords == null) return;
    setSaving(true);
    setError(null);
    try {
      const values = getValues();
      const priorOther = fullHealthRecords?.other || {};
      const merged = {
        ...(fullHealthRecords ?? {}),
        other: {
          ...values,
          ...(Array.isArray(priorOther.dischargeSummaries)
            ? { dischargeSummaries: priorOther.dischargeSummaries }
            : {}),
        },
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
      setSuccessMsg('Saved.');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [user?.uid, saving, getValues, fullHealthRecords]);

  if (loading) {
    return (
      <div className="inner">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="inner">
      <Box sx={{ mt: { xs: 1, sm: 5 }, mb: 1 }}>
        <Button
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate(masterChartPath())}
          size="small"
        >
          Back to Health Records
        </Button>
      </Box>
      <DashboardPageHeader
        title="Other History"
        subtitle="Lifestyle factors and family history."
        sx={{ mt: 0 }}
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert
          severity="success"
          onClose={() => setSuccessMsg(null)}
          sx={{ mb: 2 }}
        >
          {successMsg}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Social History
        </Typography>
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="smokingStatus"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Smoking Status"
                  fullWidth
                  placeholder="e.g., Never smoker / Former smoker (quit 2015) / Current smoker"
                  helperText="Include current status and history if applicable."
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="alcoholUse"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Alcohol Use"
                  fullWidth
                  placeholder="e.g., Occasional (1-2 drinks/week) / None"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="drugUse"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Drug Use"
                  fullWidth
                  placeholder="e.g., None / Marijuana (occasional)"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="occupation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Occupation"
                  fullWidth
                  placeholder="e.g., Office worker / Construction / Healthcare"
                  helperText="Relevant for occupational exposures."
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Family History
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Controller
            name="familyHistory"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Family Medical History"
                fullWidth
                multiline
                rows={5}
                placeholder={
                  "List significant family medical history:\n• Father: Heart disease (age 55), Diabetes\n• Mother: Breast cancer (age 60), Hypertension\n• Grandparents: Stroke, Alzheimer's"
                }
                helperText="Include conditions, relationships, and age of onset if known."
              />
            )}
          />
        </Box>
      </Paper>

      {Array.isArray(fullHealthRecords?.other?.dischargeSummaries) &&
        fullHealthRecords.other.dischargeSummaries.length > 0 && (
          <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Discharge summaries
            </Typography>
            {fullHealthRecords.other.dischargeSummaries.map((summary, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Discharge summary
                  </Typography>
                  <ProvenanceInfoButton source={summary.source} />
                </Box>
                {summary.text ? (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {summary.text}
                  </Typography>
                ) : null}
                {summary.followUpInstructions ? (
                  <Typography variant="body2" color="text.secondary">
                    Follow-up: {summary.followUpInstructions}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Paper>
        )}

      <LoadingButton
        variant="contained"
        onClick={handleSave}
        loading={saving}
        disabled={saving || loadFailed}
      >
        Save
      </LoadingButton>
    </div>
  );
};

export default OtherHistoryPage;
